import { makeStyles } from '@material-ui/styles'
import PicklistField from 'core/components/validatedForm/DropdownField'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import RadioFields from 'core/components/validatedForm/radio-fields'
import ModalForm from 'core/elements/modal/ModalForm'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { Route } from 'core/plugins/route'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import ServiceUnhealthyInfo from 'openstack/components/common/ServiceUnhealthyInfo'
import React, { useCallback } from 'react'
import useReactRouter from 'use-react-router'
import { listPorts } from '../actions'
import PortPicklist from '../network-details/PortPicklist'
import SubnetPicklist from '../network-details/SubnetPicklist'
import { createRouterInterface } from './actions'
import { IRouterDetailsPageTabs } from './model'

interface Props {
  addRoute: Route
  router: any
}

const defaultParams = {
  subnet: null,
  port: null,
  interfaceType: 'subnet',
}

export default function CreateRouterInterfaceModal({ addRoute, router }: Props) {
  const classes = useStyles()
  const { history } = useReactRouter()
  const { params, getParamsUpdater, setParams } = useParams(defaultParams)
  const { reload } = useListAction(listPorts, {})
  const { update, updating, error, reset } = useUpdateAction(createRouterInterface)

  const createInterfaceFn = async (params, routerId) => {
    const body =
      params?.interfaceType === 'subnet'
        ? {
            subnet_id: params?.subnet?.id,
          }
        : {
            port_id: params?.port?.id,
          }
    const { success } = await update({ id: routerId, body })
    return { success }
  }

  const submitForm = useCallback(async () => {
    const { success } = await createInterfaceFn(params, router.id)
    if (success) {
      reload(true, true)
      handleClose()
    }
  }, [params, router])

  const handleClose = useCallback(() => {
    setParams(defaultParams)
    reset()
    history.push(
      routeManager.routes.openstack.routerDetails.path({
        id: router.id,
        tab: IRouterDetailsPageTabs.Interfaces,
      }),
    )
  }, [router])

  return (
    <ModalForm
      route={addRoute}
      title={`Create Router Interface`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updating}
      error={error}
      submitTitle={`Create Router Interface`}
      panel="drawer"
    >
      <ServiceUnhealthyInfo action="Router Interface Creation" network />
      <FormFieldSection title="Select either a Subnet or Port to create a router interface">
        <RadioFields
          id="interfaceType"
          options={[
            {
              value: 'subnet',
              label: 'Subnet',
              extraContent: (
                <PicklistField
                  id="subnet"
                  label=""
                  DropdownComponent={SubnetPicklist}
                  value={params.subnet}
                  onChange={getParamsUpdater('subnet')}
                  className={classes.picklist}
                  disabled={params?.interfaceType !== 'subnet'}
                  required={params?.interfaceType === 'subnet'}
                />
              ),
            },
            {
              value: 'port',
              label: 'Port',
              extraContent: (
                <PicklistField
                  id="port"
                  label=""
                  DropdownComponent={PortPicklist}
                  value={params.port}
                  onChange={getParamsUpdater('port')}
                  className={classes.picklist}
                  disabled={params?.interfaceType !== 'port'}
                  required={params?.interfaceType === 'port'}
                />
              ),
            },
          ]}
          value={params?.interfaceType}
          onChange={getParamsUpdater('interfaceType')}
          data-testid="add-secret-modal-form-radio"
        />
      </FormFieldSection>
    </ModalForm>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  picklist: {
    margin: theme.spacing(1, 1, 1, 3),
  },
}))
